import {FormControl} from '@angular/forms';
import {
  ISourceOfBusinessRequest,
  SourceOfBusinessType,
} from './source-of.business';
import {Currency} from './currency';
import {IGeneralRiskRequest} from './accident-policy.models';
import {IQueryParams} from './queryparams';
import {IPolicy, RevisionType} from './policy';
import {IClient} from './clients.models';
import {IProduct} from './product.models';

export type PaymentStatus = 'Success' | 'Failed' | 'Pending' | 'Cancelled';

export type PaymentMethod =
  | 'Cash'
  | 'Card'
  | 'Wallet'
  | 'External'
  | 'PayLater'
  | 'ZamtelKwacha'
  | 'MtnMoney'
  | 'AirtelMoney'
  | 'MobileMoney'
  | 'BankTransfer'
  | 'Patumba';

export const PAYMENT_METHODS: PaymentMethod[] = [
  'Cash',
  'Card',
  'External',
  'PayLater',
  'ZamtelKwacha',
  'MtnMoney',
  'AirtelMoney',
];

export const MOBILE_MONEY_PAYMENT_METHODS: PaymentMethod[] = [
  'ZamtelKwacha',
  'MtnMoney',
  'AirtelMoney',
];

export type TransactionType =
  | 'NewBusiness'
  | 'Renewal'
  | 'Extension'
  | 'Cancellation'
  | 'Claim'
  | 'Refund';

/** Mirrors the backend PaymentType enum exactly. */
export type PaymentType =
  | 'RevisionRefund'
  | 'CancellationRefund'
  | 'CommissionPayOut'
  | 'ClaimPayout'
  | 'CommissionReversal'
  | 'WalletTopUp'
  | 'WalletWithdrawal'
  | 'Settlement'
  | 'WalletTransfer'
  | 'CampaignWalletTopUp'
  | 'DuplicatePaymentRefund'
  | 'CampaignPayout';

/** The rail a transaction went over; not the partner-configurable `PaymentGateway` enum. */
export type TransactionGateway =
  | 'Lipila'
  | 'LipilaGari'
  | 'AirtelMoney'
  | 'MtnMoney'
  | 'ZamtelKwacha'
  | 'External'
  | 'Internal'
  | 'Wallet'
  | 'LipilaBlaze'
  | 'Mba'
  | 'Patumba';

/** Which part of a policy transaction an internal wallet transfer moves. */
export type WalletTransferPortion = 'Premium' | 'Commission' | 'Combined';

export type PaymentAttemptOutcome = 'Sent' | 'Failed' | 'AdoptedFromLipila' | 'StatusUnknown' | 'RetryRequested';

/** One attempt to send a payment; failure reasons are kept here across retries. */
export interface IPaymentAttempt {
  id: number;
  createdAt: string;
  outcome: PaymentAttemptOutcome;
  /** The reference (PxnId) the attempt was sent under. */
  reference: string;
  message?: string | null;
  /** The admin who requested a retry; absent for automatic attempts. */
  actorName?: string | null;
}

export interface IPaymentMethodSelectionResult {
  paymentMethod: PaymentMethod;
  data: ICardDetails | string | null;
  isPaymentPlanEnabled?: boolean | null;
  paymentPlanConfigData?: PaymentPlanConfigData | null;
}

export interface PaymentPlanConfigData {
  proofOfPaymentUrl?: string | null;
  paymentPlanConfigId?: number | null;
  modeOfPayment?: string | null;
  customSecondInstallmentDate?: Date | null;
}

export interface MobileMoneyForm {
  phoneNumber: FormControl<string>;
}

export interface CardForm {
  city: FormControl<string>;
  country: FormControl<string>;
  address: FormControl<string>;
  zip: FormControl<number>;
}

export interface IPaymentResponse {
  referenceId: string;
  txnId?: string;
  message: string;
  status: PaymentStatus;
  policy?: IPolicy;
  redirectUrl?: string;
  warnings?: string[] | null;
}

export interface ICardPaymentRequest {
  cardNumber: string;
  cvv: string;
  cardHolderName: string;
  expiryDate: string;
  quotationId: number;
}

export interface IMobileMoneyPaymentRequest {
  accountNumber: string;
  quotationId: number;
}

export interface ICardDetails extends IMobileMoneyPaymentRequest {
  cardNumber: string;
  expiryMonth: number;
  expiryYear: number;
  cvv: number;
  city: string;
  country: string;
  address: string;
  zip: number;
}

/* issue model */
export interface IPaymentRequest {
  paymentMethod: PaymentMethod;
  cardDetails: ICardDetails | null;
  mobileMoneyAccountNumber: string | null;
  sourceOfBusiness: ISourceOfBusinessRequest;
  paymentPlanConfigData?: PaymentPlanConfigData | null;
}

export interface IGeneralPolicyRenewalRequest extends IPaymentRequest {
  startDate: Date;
  endDate: Date;
  currency: Currency;
  productId: number;
  clientId: number;
  risks: IGeneralRiskRequest[];
  territorialLimits: string[];
  clauses: string[];
}

export interface IGeneralPolicyRevisionRequest extends IPaymentRequest {
  startDate: Date;
  endDate: Date;
  currency: Currency;
  currencyRate: number;
  productId: number;
  clientId: number;
  type: RevisionType;
  risks: IGeneralRiskRequest[];
  territorialLimits: string[];
  clauses: string[];
}

export interface IGeneralAccidentExtensionRequest extends IPaymentRequest {
  endDate: Date;
  risks: IGeneralRiskRequest[];
  mobileMoneyAccountNumber: string | null;
}

export interface PaymentsQueryParams extends IQueryParams {
  paymentTypes?: PaymentType[];
  paymentGateways?: TransactionGateway[];
  needsReview?: boolean | null;
  statuses?: PaymentStatus[];
  insurerId?: number | null;
  partnerId?: number | null;
  clientId?: number;
  intermediaryId?: number | null;
  query?: string;
  startDate?: string | null;
  endDate?: string | null;
}

export interface PaymentMethodsParams {
  insurerId?: number | null;
  partnerId?: number | null;
  intermediaryId?: number | null;
  productId?: number | null;
  brokerId?: number | null;
  sourceOfBusinessType?: SourceOfBusinessType | null;
}

export interface PaymentSummary {
  client: IClient | null;
  startDate: Date | null;
  endDate: Date | null;
  product?: IProduct | null;
  premium: number;
  currency: Currency;
  insurerId?: number | null;
  intermediaryId?: number | null;
  levy?: number | null;
  policyId?: number | null;
}

export interface IStatusStats {
  totalAmount: number;
  totalCount: number;
  successAmount: number;
  successCount: number;
  pendingAmount: number;
  pendingCount: number;
  failedAmount: number;
  failedCount: number;
}
